import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Link as MUILink } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

export const COOKIE_CONSENT_KEY = "grohair_cookie_consent";
export const COOKIE_CONSENT_EVENT = "grohair-cookie-consent-changed";

const saveConsent = (status) => {
  const value = {
    status,
    analytics: status === "accepted",
    marketing: status === "accepted",
    date: new Date().toISOString(),
  };
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(value));
  window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkConsent = () => {
      let stored = null;
      try {
        stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      } catch (e) {
        stored = null;
      }
      setVisible(!stored);
    };

    checkConsent();
    window.addEventListener(COOKIE_CONSENT_EVENT, checkConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, checkConsent);
  }, []);

  if (!visible) return null;

  const handleAccept = () => {
    saveConsent("accepted");
    setVisible(false);
  };

  const handleReject = () => {
    saveConsent("rejected");
    setVisible(false);
  };

  const handleCustomize = () => {
    navigate("/cookie-policy");
  };

  return (
    <Box
      role="dialog"
      aria-label="Cookie consent"
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1400,
        backgroundColor: "#fff",
        color: "#000",
        borderTop: "1px solid #eee",
        px: { xs: 2.5, md: 5 },
        py: { xs: 2.2, md: 2 },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" },
        justifyContent: "space-between",
        gap: 2,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.12)",
      }}
    >
      <Typography sx={{ fontSize: { xs: "0.85rem", md: "0.92rem" }, lineHeight: 1.6, maxWidth: { md: "62%" } }}>
        We use cookies to enhance your browsing experience, analyze site traffic and personalize
        content. Read our{" "}
        <MUILink
          component={RouterLink}
          to="/cookie-policy"
          sx={{ color: "#e3242b", fontWeight: 700, textDecoration: "underline" }}
        >
          Cookie Policy
        </MUILink>{" "}
        to learn more about how we use cookies.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", width: { xs: "100%", md: "auto" } }}>
        <Button
          onClick={handleReject}
          variant="outlined"
          sx={{
            color: "#000",
            borderColor: "#ddd",
            backgroundColor: "#f5f5f5",
            borderRadius: "8px",
            px: 2.5,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { borderColor: "#ccc", backgroundColor: "#eee" },
          }}
        >
          Reject
        </Button>
        <Button
          onClick={handleCustomize}
          variant="outlined"
          sx={{
            color: "#e3242b",
            borderColor: "#e3242b",
            backgroundColor: "#fff",
            borderRadius: "8px",
            px: 2.5,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "rgba(227,36,43,0.06)" },
          }}
        >
          Customize
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          sx={{
            backgroundColor: "#e3242b",
            borderRadius: "8px",
            px: 3,
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: "#c81d15" },
          }}
        >
          Accept
        </Button>
      </Box>
    </Box>
  );
}
